import { databaseFieldTypeValidator } from '@databases/types/database';
import { z } from 'zod';

const fieldId = z.string().min(1, 'fieldId is required');

export const removeFieldSchema = z.object({
  fieldId
});

export const setNameSchema = z.object({
  fieldId,
  name: z.string().min(1, 'Field name cannot be empty')
});

export const setWidthSchema = z.object({
  fieldId,
  width: z
    .number()
    .int('Width must be an integer')
    .nonnegative('Width cannot be negative')
});

export const reorderSchema = z.object({
  fieldId,
  index: z
    .number()
    .int('Index must be an integer')
    .nonnegative('Index cannot be negative')
});

export const setDescriptionSchema = z.object({
  fieldId,
  description: z.string()
});

export const setMultipleSchema = z.object({
  fieldId,
  multiple: z.boolean()
});

export const setDefaultEnabledSchema = z.object({
  fieldId,
  defaultEnabled: z.boolean()
});

export const setCloudIdSchema = z.object({
  fieldId,
  cloudId: z.string().min(1, 'cloudId is required')
});

export const setTypeSchema = z.object({
  fieldId,
  fieldType: databaseFieldTypeValidator
});

export const setDependentFieldSchema = z.object({
  fieldId,
  dependentFieldId: fieldId
});

export const setNumberFormatSchema = z.object({
  fieldId,
  numberFormat: z.record(z.unknown())
});

export const getOrCreateSelectOptionSchema = z.object({
  fieldId,
  optionValue: z.string().min(1, 'Option value cannot be empty')
});

export const setShowFieldDescriptionSchema = z.object({
  fieldId,
  showDescription: z.boolean()
});

export const setPrefixOrSuffixSchema = z.object({
  fieldId,
  changeType: z.enum(['prefix', 'suffix']),
  newValue: z.string()
});

const delta = z.array(z.record(z.unknown())).min(1, 'delta is required');

export const applyNameDeltaSchema = z.object({
  fieldId,
  delta
});

export const applyDescriptionDeltaSchema = z.object({
  fieldId,
  delta
});

export type RemoveFieldInput = z.infer<typeof removeFieldSchema>;
export type SetNameInput = z.infer<typeof setNameSchema>;
export type SetWidthInput = z.infer<typeof setWidthSchema>;
export type ReorderInput = z.infer<typeof reorderSchema>;
export type SetDescriptionInput = z.infer<typeof setDescriptionSchema>;
export type SetMultipleInput = z.infer<typeof setMultipleSchema>;
export type SetDefaultEnabledInput = z.infer<typeof setDefaultEnabledSchema>;
export type SetCloudIdInput = z.infer<typeof setCloudIdSchema>;
export type SetTypeInput = z.infer<typeof setTypeSchema>;
export type SetDependentFieldInput = z.infer<typeof setDependentFieldSchema>;
export type SetNumberFormatInput = z.infer<typeof setNumberFormatSchema>;
export type GetOrCreateSelectOptionInput = z.infer<typeof getOrCreateSelectOptionSchema>;
export type SetShowFieldDescriptionInput = z.infer<typeof setShowFieldDescriptionSchema>;
export type SetPrefixOrSuffixInput = z.infer<typeof setPrefixOrSuffixSchema>;
export type ApplyNameDeltaInput = z.infer<typeof applyNameDeltaSchema>;
export type ApplyDescriptionDeltaInput = z.infer<typeof applyDescriptionDeltaSchema>;
